import {
  CloseIcon,
  FlexBox,
  JoonUIColor,
  Loader,
  Modal,
  PhotoIcon,
  QuestInstance,
  QuestStatus,
  SPACING,
  StickyNoteIcon,
  Typography,
} from "@joonapp/web-shared"
import dayjs, { Dayjs } from "dayjs"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useViewQuestModalStore } from "./useViewQuestModalStore"
import { completeQuestStatuses } from "../../../constants"
import { useFamilyQuery } from "../../../networking/queries"
import { getQuestLog } from "../../../networking/quests"
import { dateIsToday } from "../../../util/util"
import { useQuestGroupInstances } from "../useQuestGroups"

const QuestLog = () => {
  const { questInstance } = useViewQuestModalStore()
  const { questGroupInstances } = useQuestGroupInstances(questInstance)
  const { data: family } = useFamilyQuery()
  const { t } = useTranslation()
  const [data, setData] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!questGroupInstances.length) return
    const fetchQuestLogs = async () => {
      setIsLoading(true)
      const questLogs = questGroupInstances.map((quest) =>
        getQuestLog({ seriesId: quest.series.id })
      )
      const result = await Promise.all(questLogs).then((res) =>
        res.flat().sort((log1, log2) => {
          return dayjs(log2.date).diff(dayjs(log1.date))
        })
      )
      setData(result)
      setIsLoading(false)
    }

    fetchQuestLogs()
  }, [questGroupInstances])

  return (
    <table style={{ width: "100%", textAlign: "left" }}>
      <thead>
        <tr>
          <th>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              {t("QL_DATE")}
            </Typography>
          </th>
          <th>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              {t("QL_USER")}
            </Typography>
          </th>
          <th>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              {t("QL_STATUS")}
            </Typography>
          </th>
          <th>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              {t("QL_COINS")}
            </Typography>
          </th>
          <th>
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              {t("QL_DETAILS")}
            </Typography>
          </th>
        </tr>
      </thead>
      {isLoading ? (
        <tbody>
          <tr>
            <td colSpan={5}>
              <FlexBox
                direction="column"
                justify="center"
                align="center"
                wrap={false}
                style={{ paddingTop: SPACING.space6 }}
              >
                <Loader
                  size={24}
                  color={JoonUIColor.text.secondary}
                  thickness={2}
                />
              </FlexBox>
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {data?.map((questInstance: QuestInstance, i: number) => {
            const questIsComplete =
              questInstance?.status &&
              completeQuestStatuses.includes(questInstance.status)

            return (
              <tr key={i}>
                <td>
                  <Typography variant="bodySmall">
                    {dayjs(questInstance.date, "YYYY-MM-DD").format("MM/DD")}
                  </Typography>
                </td>
                <td>
                  <Typography variant="bodySmall">
                    {family?.profiles?.find(
                      (profile) => profile.user.id === questInstance.user_id
                    )?.user.name || "--"}
                  </Typography>
                </td>
                <td>
                  {getQuestPanelStatusLabel(
                    questInstance.status,
                    dayjs(questInstance.date),
                    questInstance.series.photo_auto_verify_max,
                    t
                  )}
                </td>
                <td data-tooltip-id={`instance-reward-${i}`}>
                  <Typography
                    variant={"bodySmall"}
                    color={JoonUIColor.text.primary}
                  >
                    {questIsComplete ? questInstance.redeemable_reward : "-"}
                  </Typography>
                </td>
                <td>
                  <FlexBox align="center" gap={SPACING.space1} wrap={false}>
                    <QuestNote questCompletion={questInstance} />
                    <QuestPic questCompletion={questInstance} />
                  </FlexBox>
                </td>
              </tr>
            )
          })}
        </tbody>
      )}
    </table>
  )
}

export default QuestLog

const QuestNote = ({ questCompletion }: { questCompletion: QuestInstance }) => {
  const [showModal, setShowModal] = useState(false)
  const { t } = useTranslation()

  if (!questCompletion.completion_notes && !questCompletion.verification_notes)
    return null

  return (
    <>
      <button onClick={() => setShowModal(true)}>
        <FlexBox
          style={{
            padding: `${SPACING.space05} ${SPACING.space1}`,
            background: JoonUIColor.background.lightGray,
            borderRadius: SPACING.space1,
            width: "fit-content",
          }}
          wrap={false}
          align="center"
          justify="center"
          gap={SPACING.space1}
        >
          <StickyNoteIcon size={16} color={JoonUIColor.icon.neutral} />
          <Typography variant="bodySmall">{t("QL_NOTE")}</Typography>
        </FlexBox>
      </button>
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        displayCloseIcon={false}
        animate
      >
        <FlexBox
          style={{
            padding: `${SPACING.space4} ${SPACING.space6}`,
            width: "min(95vw, 400px)",
          }}
          justify="space-between"
          align="center"
          wrap={false}
        >
          <Typography variant="h3">{t("QL_COMPLETION_NOTES")}</Typography>
          <button onClick={() => setShowModal(false)} style={{ padding: "0" }}>
            <CloseIcon color={JoonUIColor.text.primary} />
          </button>
        </FlexBox>
        <FlexBox
          style={{
            padding: `${SPACING.space0} ${SPACING.space6} ${SPACING.space6} ${SPACING.space6}`,
            width: "min(95vw, 400px)",
          }}
          gap={SPACING.space2}
          direction="column"
        >
          {questCompletion.completion_notes && (
            <FlexBox
              style={{
                padding: SPACING.space2,
                borderRadius: SPACING.space2,
                background: JoonUIColor.background.lightBlue,
              }}
            >
              <Typography
                variant="bodyBold"
                color={JoonUIColor.text.primary}
                textAlign="left"
              >
                {t("QL_SUBMITTED_NOTES")}
              </Typography>
              <Typography variant="body">
                {questCompletion.completion_notes}
              </Typography>
            </FlexBox>
          )}
          {questCompletion.verification_notes && (
            <FlexBox
              style={{
                padding: SPACING.space2,
                borderRadius: SPACING.space2,
                background: JoonUIColor.background.lightBlue,
              }}
            >
              <Typography
                variant="bodyBold"
                color={JoonUIColor.text.primary}
                textAlign="left"
              >
                {t("QL_RESPONSE_PARENTS")}
              </Typography>
              <Typography variant="body">
                {questCompletion.verification_notes}
              </Typography>
            </FlexBox>
          )}
        </FlexBox>
      </Modal>
    </>
  )
}

const QuestPic = ({ questCompletion }: { questCompletion: QuestInstance }) => {
  const [showModal, setShowModal] = useState(false)
  const { t } = useTranslation()

  if (!questCompletion.completion_photo_url) return null

  return (
    <>
      <button onClick={() => setShowModal(true)}>
        <FlexBox
          style={{
            padding: `${SPACING.space05} ${SPACING.space1}`,
            background: JoonUIColor.background.lightGray,
            borderRadius: SPACING.space1,
            width: "fit-content",
          }}
          wrap={false}
          align="center"
          justify="center"
          gap={SPACING.space1}
        >
          <PhotoIcon size={16} color={JoonUIColor.icon.neutral} />
          <Typography variant="bodySmall">{t("QL_PIC")}</Typography>
        </FlexBox>
      </button>
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        displayCloseIcon={false}
        animate
      >
        <FlexBox
          style={{
            padding: `${SPACING.space4} ${SPACING.space6}`,
            width: "min(95vw, 600px)",
          }}
          justify="space-between"
          align="center"
          wrap={false}
        >
          <Typography variant="h3">{t("QL_COMPLETION_PHOTO")}</Typography>
          <button onClick={() => setShowModal(false)} style={{ padding: "0" }}>
            <CloseIcon color={JoonUIColor.text.primary} />
          </button>
        </FlexBox>
        <FlexBox
          style={{
            padding: `${SPACING.space0} ${SPACING.space6} ${SPACING.space6} ${SPACING.space6}`,
            width: "min(95vw, 600px)",
          }}
        >
          <img
            src={questCompletion.completion_photo_url}
            alt="completion"
            style={{ width: "100%" }}
          />
        </FlexBox>
      </Modal>
    </>
  )
}

const getQuestPanelStatusLabel = (
  status: QuestStatus,
  date: Dayjs,
  photoAutoVerifyMax: number | null,
  t: (key: any) => string
) => {
  switch (status) {
    case QuestStatus.COMPLETED:
      return (
        <Typography variant="bodySmall">{t("QL_NOT_YET_VERIFIED")}</Typography>
      )
    case QuestStatus.REDEEMED:
    case QuestStatus.VERIFIED:
      if (photoAutoVerifyMax !== null) {
        return (
          <Typography variant="bodySmall">{t("QL_AI_APPROVED")}</Typography>
        )
      }
      return (
        <Typography variant="bodySmall">{t("QB_TAB_COMPLETED")}</Typography>
      )
    case QuestStatus.OPEN:
      if (dateIsToday(date)) {
        return (
          <Typography variant="bodySmall">{t("STATUS_INCOMPLETE")}</Typography>
        )
      } else
        return <Typography variant="bodySmall">{t("QL_MISSED")}</Typography>
    case QuestStatus.REJECTED:
      return (
        <Typography variant="bodySmall" color={JoonUIColor.background.alert}>
          {photoAutoVerifyMax !== null
            ? t("QL_AI_REJECTED")
            : t("QB_TAB_REJECTED")}
        </Typography>
      )
    case QuestStatus.SKIPPED:
      return (
        <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
          {t("QB_TAB_SKIPPED")}
        </Typography>
      )
    default:
      return (
        <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
          -
        </Typography>
      )
  }
}
